import { ChainlitAPI, ChainlitContext } from "@chainlit/react-client";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { App, ConfigProvider } from "antd";
import { HelmetProvider } from "react-helmet-async";
import { RecoilRoot } from "recoil";

import ConditionalWrapper from "@/base-components/ConditionalWrapper";
import UserInfoProvider from "@/components/auth/UserInfoProvider";
import UserSubscriptionProvider from "@/components/billing/UserSubscriptionProvider";
import ChainlitProvider from "@/components/chainlit/ChainlitProvider";
import MixpanelProvider from "@/components/mixpanel/MixpanelProvider";
import PosthogProvider from "@/components/posthog/PosthogProvider";
import { isFeatureEnabled } from "@/flags";
import { buildProvidersTree } from "@/lib/provider";
import { appTheme } from "@/theme";

export const Route = createRootRouteWithContext()({
  component: RootComponent,
});

const chainlitApiClient = import.meta.env.VITE_CHAINLIT_SERVER_URL
  ? new ChainlitAPI(import.meta.env.VITE_CHAINLIT_SERVER_URL)
  : null;

function RootComponent() {
  const ProvidersTree = buildProvidersTree([
    [HelmetProvider],
    [ChainlitContext.Provider, { value: chainlitApiClient }],
    [RecoilRoot],
    [ConfigProvider, { theme: appTheme }],
    [App],
    [UserInfoProvider],
    [PosthogProvider],
    [
      ConditionalWrapper,
      {
        condition: isFeatureEnabled(import.meta.env.VITE_MIXPANEL_ENABLED),
        Wrapper: MixpanelProvider,
      },
    ],
    [UserSubscriptionProvider],
    [
      ConditionalWrapper,
      {
        condition: isFeatureEnabled(import.meta.env.VITE_CHAINLIT_ENABLED),
        Wrapper: ChainlitProvider,
      },
    ],
  ]);
  return (
    // eslint-disable-next-line react-hooks/static-components
    <ProvidersTree>
      <Outlet />
      <TanStackRouterDevtools initialIsOpen={false} />
      <ReactQueryDevtools initialIsOpen={false} />
    </ProvidersTree>
  );
}
